import React, { useCallback, useRef, useState } from 'react';
import { useI18n } from '../i18n';
import { QROptions, COLOR_PRESETS } from '../lib/qrUtils';
import { Upload, Trash2, X } from 'lucide-react';

interface DesignPanelProps {
  options: QROptions;
  onChange: (options: QROptions) => void;
}

const ERROR_LEVELS: Array<{ key: 'L' | 'M' | 'Q' | 'H'; value: string }> = [
  { key: 'L', value: 'L' },
  { key: 'M', value: 'M' },
  { key: 'Q', value: 'Q' },
  { key: 'H', value: 'H' },
];

export function DesignPanel({ options, onChange }: DesignPanelProps) {
  const { t } = useI18n();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleColorChange = useCallback(
    (field: 'fgColor' | 'bgColor', value: string) => {
      onChange({ ...options, [field]: value });
    },
    [options, onChange]
  );

  const handlePresetClick = useCallback(
    (preset: (typeof COLOR_PRESETS)[number]) => {
      onChange({ ...options, fgColor: preset.fg, bgColor: preset.bg });
    },
    [options, onChange]
  );

  const handleFile = useCallback(
    (file: File) => {
      if (!file) return;
      const validTypes = ['image/png', 'image/jpeg', 'image/svg+xml'];
      if (!validTypes.includes(file.type)) {
        alert('Please upload a PNG, JPG, or SVG file.');
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        alert('File size must be under 2MB.');
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        onChange({ ...options, logo: result });
      };
      reader.readAsDataURL(file);
    },
    [options, onChange]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
      e.target.value = '';
    },
    [handleFile]
  );

  const removeLogo = useCallback(() => {
    onChange({ ...options, logo: null });
  }, [options, onChange]);

  const handleLevelChange = useCallback(
    (level: 'L' | 'M' | 'Q' | 'H') => {
      onChange({ ...options, level });
    },
    [options, onChange]
  );

  const handleLogoWhiteBorderChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...options, logoWhiteBorder: e.target.checked });
    },
    [options, onChange]
  );

  const handleLogoBorderSizeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...options, logoBorderSize: parseInt(e.target.value, 10) });
    },
    [options, onChange]
  );

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
      <h2 className="text-lg font-semibold text-foreground">
        {t('qr.design.title')}
      </h2>

      {/* Color Settings */}
      <div className="space-y-4">
        {/* Foreground Color */}
        <div className="flex items-center justify-between gap-4">
          <label className="text-sm font-medium text-muted-foreground shrink-0">
            {t('qr.design.foregroundColor')}
          </label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={options.fgColor}
              onChange={(e) => handleColorChange('fgColor', e.target.value)}
              className="w-8 h-8 p-0 border-0 rounded cursor-pointer"
              style={{ background: 'none' }}
            />
            <span className="text-sm font-mono text-muted-foreground w-16 text-right uppercase">
              {options.fgColor}
            </span>
          </div>
        </div>

        {/* Background Color */}
        <div className="flex items-center justify-between gap-4">
          <label className="text-sm font-medium text-muted-foreground shrink-0">
            {t('qr.design.backgroundColor')}
          </label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={options.bgColor}
              onChange={(e) => handleColorChange('bgColor', e.target.value)}
              className="w-8 h-8 p-0 border-0 rounded cursor-pointer"
              style={{ background: 'none' }}
            />
            <span className="text-sm font-mono text-muted-foreground w-16 text-right uppercase">
              {options.bgColor}
            </span>
          </div>
        </div>

        {/* Color Presets */}
        <div>
          <label className="text-xs text-muted-foreground block mb-2">
            {t('qr.design.presets')}
          </label>
          <div className="flex flex-wrap gap-2">
            {COLOR_PRESETS.map((preset) => (
              <button
                key={preset.name}
                onClick={() => handlePresetClick(preset)}
                className="w-8 h-8 rounded-md transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-ring"
                style={{
                  background: `linear-gradient(135deg, ${preset.fg} 50%, ${preset.bg} 50%)`,
                }}
                title={`${preset.name}: ${preset.fg} / ${preset.bg}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Logo Upload */}
      <div className="space-y-3">
        <h3 className="text-sm font-medium text-foreground">
          {t('qr.design.logo.title')}
        </h3>

        {options.logo ? (
          <div className="space-y-3">
            <div className="relative w-full h-32 bg-muted rounded-lg flex items-center justify-center overflow-hidden">
              <img
                src={options.logo}
                alt="Logo preview"
                className="max-w-full max-h-full object-contain"
              />
              <button
                onClick={removeLogo}
                className="absolute top-2 right-2 w-7 h-7 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center hover:opacity-90 transition-opacity"
                title={t('qr.design.logo.remove')}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* White Border Checkbox */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={options.logoWhiteBorder}
                onChange={handleLogoWhiteBorderChange}
                className="rounded border-border text-primary focus:ring-primary"
              />
              <span className="text-sm text-muted-foreground">
                {t('qr.design.logo.whiteBorder')}
              </span>
            </label>

            {/* Border Size Slider */}
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  {t('qr.design.logo.borderSize')}
                </span>
                <span className="text-sm font-medium text-foreground">
                  {options.logoBorderSize}px
                </span>
              </div>
              <input
                type="range"
                min={2}
                max={16}
                value={options.logoBorderSize}
                onChange={handleLogoBorderSizeChange}
                className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
              />
            </div>

            <button
              onClick={removeLogo}
              className="flex items-center gap-2 text-sm text-destructive hover:text-destructive/80 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              {t('qr.design.logo.remove')}
            </button>
          </div>
        ) : (
          <div
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={`
              border-dashed border-2 border-border rounded-lg
              p-6 flex flex-col items-center justify-center gap-2
              cursor-pointer transition-colors
              ${isDragging ? 'border-primary bg-primary/5' : 'hover:border-muted-foreground/50'}
            `}
          >
            <Upload className="w-8 h-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground text-center">
              {t('qr.design.logo.dragDrop')}
            </p>
            <p className="text-xs text-muted-foreground/60">
              PNG, JPG, SVG · Max 2MB
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".png,.jpg,.jpeg,.svg"
              onChange={handleInputChange}
              className="hidden"
            />
          </div>
        )}
      </div>

      {/* Error Correction Level */}
      <div className="space-y-3">
        <h3 className="text-sm font-medium text-foreground">
          {t('qr.design.errorCorrection')}
        </h3>
        <div className="flex gap-1 bg-muted rounded-lg p-1">
          {ERROR_LEVELS.map(({ key }) => (
            <button
              key={key}
              onClick={() => handleLevelChange(key)}
              className={`
                flex-1 py-2 px-1 text-sm font-medium rounded-md transition-colors
                ${
                  options.level === key
                    ? 'bg-primary text-white'
                    : 'bg-transparent text-muted-foreground hover:text-foreground'
                }
              `}
            >
              {t(`qr.design.levels.${key}`)}
            </button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          {t('qr.design.levelHint')}
        </p>
      </div>
    </div>
  );
}
